import { useCallback, useEffect, useState } from 'react';
import { useIsPasswordlessCheckoutEnabled } from 'src/scenes/LoginPage/Modal/LoginPasscode/LoginPasscode.hooks';
import { useCartPlanSelector, useCartBreakdownSelector } from 'src/store/queries/cart/useCart.selector';
import useCartMutationRemoveSku from 'src/store/queries/cart/useCartMutation.removesku';
import useCartMutationRemoveplan from 'src/store/queries/cart/useCartMutation.removeplan';
import useCartMutationStartBooking, { handleSingleServiceBookingSuccess } from 'src/store/queries/cart/useCartMutation.startbooking';
import useCartMutationFind from 'src/store/queries/cart/useCartMutation.find';
import { useRouter } from 'next/router';
import { addSkuPath, homePath } from 'src/constants/paths';
import useCartCache from 'src/store/queries/cart/useCart.cache';
import { simplifyBreakdownItemForDisplay } from './CartNavigationDisplay.utils';
import { ITEM_REMOVAL_TOAST_MESSAGES } from 'src/constants/cart';

/**
 * This is being used for Single Service carts, so we can assume that the
 * zero index item is the only item we need to worry about.
 */
export const FIRST_ITEM_INDEX = 0;

/**
 * Provides navigation functions for the cart icon display in the top nav header.
 */
export const useCartNavigationDisplay = () => {
  const router = useRouter();

  const isPasswordlessCheckoutEnabled = useIsPasswordlessCheckoutEnabled();

  const cart = useCartCache();

  // *****************
  // START BOOKING
  // *****************
  const isInBookingFlow = (pathname = '') => pathname.includes('/cart/booking');
  const startBookingMutation = useCartMutationStartBooking();
  const startBookingProcess = () =>
    startBookingMutation.mutate(undefined, {
      onSuccess: () => handleSingleServiceBookingSuccess({ router, isPasswordless: isPasswordlessCheckoutEnabled }),
    });

  // **********************
  // SKU/SERVICE RELATED
  // **********************
  const [showRemoveServiceModal, setShowRemoveServiceModal] = useState(false);
  const toggleRemoveServiceModal = () => setShowRemoveServiceModal(!showRemoveServiceModal);

  const getEditSkuLink = (skuId: string) => `${addSkuPath(skuId)}?mode=edit&index=${FIRST_ITEM_INDEX}`;
  const removeItemMutation = useCartMutationRemoveSku();
  const handleRemoveSku = (pathname = '') => {
    // mutate here
    removeItemMutation.mutate({ idx: FIRST_ITEM_INDEX, toastMessage: ITEM_REMOVAL_TOAST_MESSAGES.SERVICE });
    if (isInBookingFlow(pathname)) {
      router.push(homePath());
    }
  };

  // **********************
  // PLAN RELATED
  // **********************
  const [showRemovePlanModal, setShowRemovePlanModal] = useState(false);
  const toggleRemovePlanModal = () => setShowRemovePlanModal(!showRemovePlanModal);

  const cartedPlan = useCartPlanSelector();
  const removePlanMutation = useCartMutationRemoveplan();
  const handleRemovePlan = (pathname = '') => {
    removePlanMutation.mutate({ toastMessage: ITEM_REMOVAL_TOAST_MESSAGES.MEMBERSHIP });

    const cartHasNoItems = cart.items.length === 0;
    const shouldRedirectToHome = isInBookingFlow(pathname) && cartHasNoItems;
    if (shouldRedirectToHome) {
      router.push(homePath());
    }
  };

  // ************************
  // CART BREAKDOWN RELATED
  // ************************
  const cartBreakdown = useCartBreakdownSelector();
  const breakdownItems = cartBreakdown?.items || [];

  const simplifiedBreakdownItems = breakdownItems.map(item => simplifyBreakdownItemForDisplay(item));

  const findCartMutation = useCartMutationFind();
  const findCart = useCallback(() => findCartMutation.mutate(), [findCartMutation]);
  const cartHasNoBreakdown = cart?.id && !cart?.breakdown;
  useEffect(() => {
    if (cartHasNoBreakdown) {
      // The initial cart has no breakdown, so we need to find it
      findCart();
    }
  }, [cartHasNoBreakdown, findCart]);

  return {
    /** Breakdown of items in the cart */
    breakdownItems: simplifiedBreakdownItems,
    /** Plan in the cart */
    cartedPlan,
    /** Returns a link to edit a sku */
    getEditSkuLink,
    /** Is the cart in the booking flow? */
    isInBookingFlow,
    /** Callback for removing a plan */
    removePlan: handleRemovePlan,
    /** Callback for removing a sku */
    removeSku: handleRemoveSku,
    /** Show the remove plan modal? */
    showRemovePlanModal,
    /** Show the remove service modal? */
    showRemoveServiceModal,
    /** Callback for starting the booking process */
    startBookingProcess,
    /** Toggle the remove plan modal */
    toggleRemovePlanModal,
    /** Toggle the remove service modal */
    toggleRemoveServiceModal,
  };
};
